import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Loader2, CheckCircle2, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';

const ScanSupplierInvoicePage = () => {
  const { sessionId } = useParams();
  const [status, setStatus] = useState('ready');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setStatus('uploading');
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      setStatus('processing');
      await axios.post(`${API}/supplier-invoices/scan-sessions/${sessionId}/upload`, formData);
      setStatus('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du scan');
      setStatus('error');
    }
  };

  const retry = () => {
    setStatus('ready');
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900">Vortekio</h1>
          <p className="text-sm text-slate-500 mt-1">Scanner une facture fournisseur</p>
        </div>

        {status === 'ready' && (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-amber-300 rounded-2xl p-8 text-center bg-white cursor-pointer hover:border-amber-500 hover:bg-amber-50/50 transition-all active:scale-95"
              onClick={() => fileInputRef.current?.click()}
              data-testid="capture-area"
            >
              <Camera className="w-16 h-16 mx-auto text-amber-500 mb-4" />
              <p className="text-lg font-semibold text-slate-800">Photographier la facture</p>
              <p className="text-sm text-slate-500 mt-1">Appuyez pour ouvrir l'appareil photo</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={handleCapture}
              data-testid="mobile-scan-input"
            />
            <p className="text-xs text-center text-slate-400">
              Photographiez la facture bien a plat, montants et TVA bien visibles
            </p>
          </div>
        )}

        {(status === 'uploading' || status === 'processing') && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-200">
            <Loader2 className="w-16 h-16 mx-auto text-amber-500 animate-spin mb-4" />
            <p className="text-lg font-semibold text-slate-800">
              {status === 'uploading' ? 'Envoi de la photo...' : 'Analyse en cours...'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              L'IA extrait les montants et la TVA
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-emerald-200">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500 mb-4" />
            <p className="text-lg font-semibold text-slate-800">Facture scannee !</p>
            <p className="text-sm text-slate-500 mt-1">
              Les données ont été envoyées.<br />Vous pouvez fermer cette page.
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-red-200">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <p className="text-lg font-semibold text-slate-800">Erreur</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <Button onClick={retry} className="mt-4" variant="outline" data-testid="retry-scan-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Reessayer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanSupplierInvoicePage;
